import { ClientGameState, PlayerPosition, HandCompleteMessage, isTeamGameScore, GAME_WIDTH } from '@hokm/shared';
import { Scoreboard, ScoreboardSlot } from '../../../objects/Scoreboard';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { toDisplayPosition } from './PositionHelpers';

/**
 * Get scoreboard position based on player count.
 * - 3P: Top center (opponents at corners)
 * - 2P/4P: Right side to avoid overlap with north player
 */
export function getScoreboardPosition(playerCount: number): { x: number; y: number } {
  const CENTER_X = GAME_WIDTH / 2;
  if (playerCount === 3) {
    return { x: CENTER_X, y: 85 };
  }
  // 2P and 4P: top-right to avoid overlap with opponent at top center
  return { x: GAME_WIDTH - 200, y: 100 };
}

/**
 * Get all opponent slots for a given winner slot.
 */
export function getOpponentSlots(winnerSlot: ScoreboardSlot, playerCount: number): ScoreboardSlot[] {
  if (playerCount === 4) {
    // Team mode: winner is 0 or 2, loser is the other
    return winnerSlot === 0 ? [2] : [0];
  } else if (playerCount === 3) {
    // 3P: all slots except winner
    const allSlots: ScoreboardSlot[] = [0, 1, 2];
    return allSlots.filter((s) => s !== winnerSlot);
  } else {
    // 2P: winner is 0 or 2, loser is the other (slot 1 unused)
    return winnerSlot === 0 ? [2] : [0];
  }
}

/**
 * Convert a player ID to a scoreboard slot.
 * Slot 0 = local player/team (bottom)
 * Slot 1 = WEST opponent
 * Slot 2 = NORTH opponent / opponent team
 * Slot 3 = EAST opponent (4P FFA only)
 */
export function getSlot(state: ClientGameState | null, playerId: string): ScoreboardSlot {
  if (!state) return 0;

  const playerCount = state.playerCount;

  if (playerCount === 4) {
    // Team mode - slot 0 = local team, slot 2 = opponent team
    const player = state.players.find((p) => p?.id === playerId);
    if (!player) return 0;
    const isTeamA = player.position % 2 === 0;
    const isLocalTeamA = state.myPosition % 2 === 0;
    const isLocalTeam = isTeamA === isLocalTeamA;
    return isLocalTeam ? 0 : 2;
  }

  // Individual mode (2P/3P)
  if (playerId === state.myPlayerId) return 0;

  const player = state.players.find((p) => p?.id === playerId);
  if (!player) return 0;

  const displayPos = toDisplayPosition(player.position, state.myPosition, playerCount);
  if (displayPos === PlayerPosition.WEST) return 1;
  if (displayPos === PlayerPosition.NORTH) return 2;
  if (displayPos === PlayerPosition.EAST) return 3;

  return 0;
}

/**
 * Configure scoreboard player slots based on game mode.
 */
export function configureScoreboardPlayers(
  scoreboard: Scoreboard,
  state: ClientGameState,
  playerCount: number,
  isTeamMode: boolean
): void {
  const slots: { slot: ScoreboardSlot; color: number }[] = [];

  if (isTeamMode) {
    // 4P Team mode - 2 teams: local at bottom (slot 0), opponent at top (slot 2)
    slots.push({ slot: 0, color: PERSIAN_THEME.colors.teamA });
    slots.push({ slot: 2, color: PERSIAN_THEME.colors.teamB });
  } else if (playerCount === 3) {
    // 3P Individual mode - local at bottom, opponents at west and north
    slots.push({ slot: 0, color: PERSIAN_THEME.colors.player1 });
    slots.push({ slot: 1, color: PERSIAN_THEME.colors.player2 });
    slots.push({ slot: 2, color: PERSIAN_THEME.colors.player3 });
  } else {
    // 2P Individual mode - local at bottom (slot 0), opponent at top (slot 2)
    slots.push({ slot: 0, color: PERSIAN_THEME.colors.player1 });
    slots.push({ slot: 2, color: PERSIAN_THEME.colors.player2 });
  }

  scoreboard.setSlots(slots);

  // Update trick dots with current hand score
  if (isTeamMode) {
    const isLocalTeamA = state.myPosition % 2 === 0;
    const handScore = state.handScore as { teamA: number; teamB: number };
    const localTricks = isLocalTeamA ? handScore.teamA : handScore.teamB;
    const opponentTricks = isLocalTeamA ? handScore.teamB : handScore.teamA;
    scoreboard.setSlotTricks(0, localTricks);
    scoreboard.setSlotTricks(2, opponentTricks);
  } else {
    const handScore = state.handScore as Record<string, number>;
    if (playerCount === 3) {
      const localPlayer = state.players.find((p) => p.id === state.myPlayerId);
      const opponents = state.players.filter((p) => p.id !== state.myPlayerId);
      const westOpponent = opponents.find(
        (p) => toDisplayPosition(p.position, state.myPosition, playerCount) === PlayerPosition.WEST
      );
      const northOpponent = opponents.find(
        (p) => toDisplayPosition(p.position, state.myPosition, playerCount) === PlayerPosition.NORTH
      );
      scoreboard.setSlotTricks(0, localPlayer ? handScore[localPlayer.id] || 0 : 0);
      if (westOpponent) scoreboard.setSlotTricks(1, handScore[westOpponent.id] || 0);
      if (northOpponent) scoreboard.setSlotTricks(2, handScore[northOpponent.id] || 0);
    } else {
      // 2P
      const localPlayer = state.players.find((p) => p.id === state.myPlayerId);
      const opponent = state.players.find((p) => p.id !== state.myPlayerId);
      scoreboard.setSlotTricks(0, localPlayer ? handScore[localPlayer.id] || 0 : 0);
      scoreboard.setSlotTricks(2, opponent ? handScore[opponent.id] || 0 : 0);
    }
  }
}

/**
 * Update scoreboard with current game state.
 */
export function updateScoreboard(scoreboard: Scoreboard, state: ClientGameState): void {
  const isTeamMode = isTeamGameScore(state.gameScore);
  const playerCount = state.playerCount as 2 | 3 | 4;

  scoreboard.setVisible(true);
  scoreboard.setMode(isTeamMode);
  scoreboard.setGameConfig(state.pointsToWin as 3 | 7, playerCount);

  // Dynamic positioning based on player count
  const pos = getScoreboardPosition(playerCount);
  scoreboard.setPosition(pos.x, pos.y);

  // Configure per-player dots
  configureScoreboardPlayers(scoreboard, state, playerCount, isTeamMode);

  if (isTeamMode) {
    const isLocalTeamA = state.myPosition % 2 === 0;
    const gameScore = state.gameScore as { teamA: number; teamB: number };

    const localGameScore = isLocalTeamA ? gameScore.teamA : gameScore.teamB;
    const opponentGameScore = isLocalTeamA ? gameScore.teamB : gameScore.teamA;

    scoreboard.setSlotScore(0, localGameScore);
    scoreboard.setSlotScore(2, opponentGameScore);
  } else {
    // Individual mode (2P/3P) - set per-player game scores
    const gameScore = state.gameScore as Record<string, number>;
    const localPlayer = state.players.find((p) => p.id === state.myPlayerId);
    const opponents = state.players.filter((p) => p.id !== state.myPlayerId);

    if (localPlayer) {
      scoreboard.setSlotScore(0, gameScore[localPlayer.id] || 0);
    }

    opponents.forEach((opponent) => {
      const slot = getSlot(state, opponent.id);
      scoreboard.setSlotScore(slot, gameScore[opponent.id] || 0);
    });
  }

  // Update trump if set
  if (state.trumpSuit) {
    scoreboard.setTrumpSuit(state.trumpSuit, false);
  }
}

/**
 * Handle hand completion - animate scoreboard celebration.
 */
export function showHandResult(scoreboard: Scoreboard, state: ClientGameState | null, data: HandCompleteMessage): void {
  const playerCount = state?.playerCount || 4;

  if (data.mode === 'team') {
    const isLocalTeamA = (state?.myPosition ?? 0) % 2 === 0;
    const winningTeamIsA = data.winningTeam === 'A';
    const isLocalWin = winningTeamIsA === isLocalTeamA;

    const newScore = data.winningTeam === 'A' ? data.newScore.teamA : data.newScore.teamB;
    const winnerSlot: ScoreboardSlot = isLocalWin ? 0 : 2;
    const loserSlots = getOpponentSlots(winnerSlot, playerCount);

    scoreboard.animateSlotPointScored(winnerSlot, newScore);
    scoreboard.animateTricksToSlot(winnerSlot, newScore, loserSlots);
  } else {
    const newScore = data.winnerId ? data.newScore[data.winnerId] || 0 : 0;

    if (data.winnerId) {
      const winnerSlot = getSlot(state, data.winnerId);
      const loserSlots = getOpponentSlots(winnerSlot, playerCount);

      scoreboard.animateSlotPointScored(winnerSlot, newScore);
      scoreboard.animateTricksToSlot(winnerSlot, newScore, loserSlots);
    }
  }

  // Kot/Hakem Koti bonus
  if (data.isKot || data.isHakemKoti) {
    scoreboard.celebrateSpecialScore();
  }
}
